import { extractEntries } from "./extract-entries";
import {
  emptyString,
  filterLinkTitle,
  notEmptyString,
  validDateString,
} from "./validators";

export class Tag {
  slug = "";
  text = "";
  count = -1;
  index = -1;

  constructor(inData = null, index = -1) {
    extractEntries(inData).forEach((entry) => {
      const [key, val] = entry;
      if (typeof val === "string") {
        switch (key) {
          case "key":
          case "slug":
            this.slug = val;
            break;
          case "name":
          case "text":
            this.text = val;
            break;
        }
      } else if (typeof val === "number" && ["total", "count"].includes(key)) {
        this.count = val;
      }
    });
    if (index >= 0) {
      this.index = index;
    }
  }

  get key() {
    return ["tag", this.slug, this.index].join("-");
  }
}

export class Contact {
  type = "email";
  identifier = "";
  notes = "";

  constructor(inData = null) {
    extractEntries(inData).forEach((entry) => {
      const [key, val] = entry;
      if (typeof val === "string") {
        switch (key) {
          case "type":
          case "identifier":
          case "notes":
            this[key] = val;
            break;
        }
      }
    });
  }

  hasNotes() {
    return notEmptyString(this.notes, 2);
  }
}

export class Link {
  url = "";
  title = "";
  description = "";

  constructor(inData = null) {
    extractEntries(inData).forEach((entry) => {
      const [key, val] = entry;
      if (typeof val === "string") {
        switch (key) {
          case "url":
          case "title":
          case "description":
            this[key] = val;
            break;
        }
      }
    });
  }

  itemKey(prefix = "link-field", index = 0, subIndex = -1) {
    const urlPart = this.url.length > 3 ? this.url.split("://").pop() : "__";
    const parts = [prefix, urlPart, index];
    if (subIndex >= 0) {
      parts.push(subIndex);
    }
    return parts.join("-");
  }

  get hasTitle() {
    return notEmptyString(this.title);
  }

  get hasUrl() {
    return notEmptyString(this.url, 5);
  }

  get displayTitle() {
    return this.hasTitle ? this.title : this.url.replace(/^\w+:\/\//, "");
  }

  get hasDescription() {
    return notEmptyString(this.description, 1);
  }
}

export class Statement {
  type: string;
  text: string;
  image = "";
  alt: string;
  attributes: any = {};

  constructor(inData = null) {
    extractEntries(inData).forEach((entry) => {
      const [key, val] = entry;
      if (typeof val === "string") {
        switch (key) {
          case "type":
          case "text":
          case "image":
          case "alt":
            this[key] = val;
            break;
        }
      } else if (val instanceof Object) {
        if (key === "attributes") {
          this.attributes = val;
        }
        /* else if (key === "cloudinary") {
          const attrKeys = Object.keys(this.attributes);
          if (attrKeys.length < 2) {
            const { originalWidth, originalHeight } = val as any;
            if (originalHeight > 0 && originalWidth > 0) {
              this.attributes = {
                width: originalWidth,
                height: originalHeight,
              }
            }
          }
        } */
      }
    });
  }

  get hasImage() {
    return notEmptyString(this.image, 6);
  }
}

export class Member {
  displayName = "";
  identifier = "";
  description = "";
  slug = "";
  submission = "";
  isLead = false;

  constructor(inData = null) {
    extractEntries(inData).forEach((entry) => {
      const [key, val] = entry;
      if (typeof val === "string") {
        switch (key) {
          case "displayName":
          case "identifier":
          case "description":
          case "slug":
          case "submission":
            this[key] = val;
            break;
        }
      } else if (key === "isLead") {
        this.isLead = !!val;
      } else if (key === "submission") {
        if (val) {
          this.submission = val.toString();
        }
      }
    });
  }

  get valid() {
    return notEmptyString(this.displayName) && notEmptyString(this.identifier);
  }

  get hasSubmission() {
    return (
      this.valid &&
      notEmptyString(this.slug) &&
      notEmptyString(this.submission, 16)
    );
  }

  itemKey(index: number): string {
    return ["member-field", index].join("-");
  }
}

export class MediaItem {
  _id: string;
  filename = "";
  sourceFilename: string;
  mime = "";
  source = "local";
  size: number;
  attributes: any;
  type: string;
  title: string;
  alt: string;
  showTitle = true;
  description: string;
  url: string;
  displayOptions: any = {};

  constructor(inData = null) {
    extractEntries(inData).forEach((entry) => {
      const [key, val] = entry;
      if (typeof val === "string") {
        switch (key) {
          case "_id":
          case "filename":
          case "sourceFilename":
          case "type":
          case "url":
          case "title":
          case "alt":
          case "mime":
          case "source":
          case "description":
            this[key] = val;
            break;
        }
      } else if (typeof val === "number") {
        switch (key) {
          case "size":
            this[key] = val;
            break;
        }
      } else if (typeof val === "boolean" && key === "showTitle") {
        this.showTitle = val;
      } else if (val instanceof Object) {
        switch (key) {
          case "attributes":
          case "displayOptions":
            this[key] = val;
            break;
        }
      }
    });
  }

  get isUntitled() {
    return notEmptyString(this.title, 2) && this.title.trim() !== "[untitled]";
  }

  get provider() {
    return notEmptyString(this.mime) ? this.mime.split("/").pop() : "";
  }

  get name() {
    return this.isUntitled ? this.title : this.shortSourceFilename;
  }

  get shortSourceFilename() {
    let refName =
      typeof this.sourceFilename === "string" ? this.sourceFilename : "";
    if (notEmptyString(refName, 16)) {
      const segments = refName.split("/");
      refName = segments.pop();
    }
    return refName;
  }

  get isImage() {
    return (
      this.type === "image" &&
      notEmptyString(this.filename, 8) &&
      (["local", "cloudinary"].includes(this.source) ||
        /res\.cloudinary\./.test(this.filename))
    );
  }

  get isModel() {
    return this.type === "model" && notEmptyString(this.filename, 8);
  }

  get isMultiMedia() {
    return !this.isImage && !this.isModel && !this.isText && !this.isPdf;
  }

  get isPdf() {
    return this.type === "document" && this.mime.endsWith("pdf");
  }

  get isText() {
    return this.type === "text";
  }

  get hasUri() {
    return this.type !== "text" && notEmptyString(this.filename, 6);
  }

  get isValid() {
    return (
      (notEmptyString(this._id, 12) && notEmptyString(this.filename, 8)) ||
      (this.isText &&
        notEmptyString(this.title) &&
        notEmptyString(this.description))
    );
  }

  get needsRequiredFields() {
    if (this.isText) {
      return emptyString(this.title);
    } else {
      return (
        notEmptyString(this.filename, 5) &&
        emptyString(this.alt) &&
        !this.isText &&
        !this.isPdf
      );
    }
  }

  get hasDescription() {
    return notEmptyString(this.description, 2);
  }

  get hasUrl() {
    return notEmptyString(this.url, 6);
  }

  get hasAlt() {
    return notEmptyString(this.alt, 1) && !this.isText;
  }
}

export class Work {
  _id: string;
  title = "";
  showTitle = true;
  slug: string;
  type = "work";
  eventLink = "";
  medium: string;
  mediaItems: MediaItem[] = [];
  year: number;
  themes: Tag[] = [];
  links: Link[] = [];
  description: string;
  shopLink: string;
  size: string;
  explicit = false;
  displayOptions: any = {};

  constructor(inData = null) {
    extractEntries(inData).forEach((entry) => {
      const [key, val] = entry;
      if (typeof val === "string") {
        switch (key) {
          case "_id":
          case "title":
          case "slug":
          case "type":
          case "eventLink":
          case "medium":
          case "description":
          case "shopLink":
          case "size":
            this[key] = val;
            break;
        }
      } else if (typeof val === "number") {
        switch (key) {
          case "year":
            this[key] = val;
            break;
        }
      } else if (typeof val === "boolean") {
        switch (key) {
          case "explicit":
          case "showTitle":
            this[key] = val;
            break;
        }
      } else if (val instanceof Array) {
        switch (key) {
          case "mediaItems":
            this.mediaItems = val.map((item) => new MediaItem(item));
            break;
          case "links":
            this.links = val.map((item) => new Link(item));
            break;
          case "themes":
            this.themes = val.map((item) => new Tag(item));
            break;
        }
      } else if (key === "displayOptions" && val instanceof Object) {
        this.displayOptions = val;
      }
    });
  }

  get hasDescription() {
    return notEmptyString(this.description, 2);
  }

  get numMediaItems() {
    return this.mediaItems.length;
  }

  get hasTextUrl() {
    return notEmptyString(this.shopLink, 5);
  }

  get hasMainMediaItems(): boolean {
    return this.mainMediaItems.length > 0;
  }

  get hasMediaItems() {
    return this.mediaItems.length > 0;
  }

  get hasLinks(): boolean {
    return this.links.filter(filterLinkTitle).length > 0;
  }

  get hasThemes() {
    return this.themes.length > 0;
  }

  get themeList() {
    return this.hasThemes ? this.themes.map((t) => t.text).join(", ") : "";
  }

  get hasSize() {
    return notEmptyString(this.size);
  }

  get hasMedium() {
    return notEmptyString(this.medium);
  }

  get mainMediaItems() {
    return this.mediaItems.filter((mi) => mi.type !== "document");
  }

  get docItems() {
    return this.mediaItems.filter((mi) => mi.type === "document");
  }

  get hasDocItems(): boolean {
    return this.docItems.length > 0;
  }
}

export class Submission {
  _id = "";
  user = "";
  mode = "student";
  members: Member[] = [];
  firstName = "";
  lastName = "";
  displayName = "";
  description = "";
  slug = "";
  contacts: Array<Contact> = [];
  school = "";
  programme = "";
  course = "";
  programmeCode = "";
  courseCode = "";
  notes = "";
  statements: Array<Statement> = [];
  displayOptions: any = {};
  links: Array<Link> = [];
  themes: Array<Tag> = [];
  subthemes: Array<Tag> = [];
  tags: Array<Tag> = [];
  sponsors: Array<Link> = [];
  works: Array<Work> = [];
  ineligible = false;
  showQrCode = false;
  deadlineException = false;
  deadlineEnd = new Date();
  submitted = false;
  createdAt: Date;
  modifiedAt: Date;

  constructor(inData = null, testMode = false) {
    extractEntries(inData).forEach((entry) => {
      const [key, val] = entry;
      if (typeof val === "string") {
        switch (key) {
          case "_id":
          case "firstName":
          case "lastName":
          case "displayName":
          case "description":
          case "slug":
          case "school":
          case "programme":
          case "course":
          case "programmeCode":
          case "courseCode":
          case "notes":
          case "user":
            this[key] = val;
            break;
          case "createdAt":
          case "modifiedAt":
          case "deadlineEnd":
            this[key] = validDateString(val, true) ? new Date(val) : new Date();
            break;
        }
      } else if (typeof val === "boolean") {
        switch (key) {
          case "showQrCode":
          case "deadlineException":
          case "submitted":
            this[key] = val;
            break;
          case "ineligible":
            this.ineligible = testMode ? false : val;
            break;
        }
      } else if (val instanceof Array) {
        switch (key) {
          case "contacts":
            this.contacts = val.map((item) => new Contact(item));
            break;
          case "statements":
            this.statements = val.map((item) => new Statement(item));
            break;
          case "members":
            this.members = val.map((item) => new Member(item));
            break;
          case "works":
            this.works = val.map((item) => new Work(item));
            break;
          case "themes":
          case "subthemes":
          case "tags":
            this[key] = val.map((item) => new Tag(item));
            break;
          case "links":
          case "sponsors":
            this[key] = val.map((item) => new Link(item));
            break;
        }
      } else if (val instanceof Object) {
        if (key === "user") {
          Object.entries(val).forEach((subEntry) => {
            const [k, v] = subEntry;
            if (k === "_id" && typeof v === "string") {
              this.user = v;
            }
          });
        } else if (key === "displayOptions") {
          this.displayOptions = val;
        }
      }
    });
  }

  findWork(workId: string) {
    return this.works.find((w) => w._id === workId);
  }

  findStatement(type: string) {
    const st = this.statements.find((st) => st.type === type);
    if (st) {
      return st;
    } else {
      return new Statement({ type });
    }
  }

  get modifiedTs() {
    return this.modifiedAt instanceof Date ? this.modifiedAt.getTime() : 0;
  }

  get hasContacts() {
    return (
      this.contacts.filter((c) => notEmptyString(c.identifier, 3)).length > 0
    );
  }

  get groupTheme(): Tag {
    if (this.themes.length > 0) {
      return new Tag(this.themes[0]);
    } else {
      return new Tag();
    }
  }

  get groupMode() {
    return ["group", "project", "projects"].includes(this.mode);
  }

  get themeList() {
    return this.themes.length > 0
      ? this.themes.map((row) => row.text).join(", ")
      : "";
  }

  get subthemeList() {
    return this.subthemes.length > 0
      ? this.subthemes.map((row) => row.text).join(", ")
      : "";
  }

  get hasTags() {
    return this.tags.length > 0;
  }

  get tagList() {
    return this.tags.length > 0
      ? this.tags
          .map((row) => {
            const txt = row.text.replace(/^#/, "");
            return `#${txt}`;
          })
          .join(", ")
      : "";
  }

  get themeInfo() {
    return [this.themeList, this.subthemeList].join(" / ");
  }

  get hasLinks() {
    return this.links.length > 0;
  }

  get hasSponsors() {
    return this.sponsors.length > 0;
  }

  get selectedThemeSlugs(): string[] {
    return [
      ...this.themes.map((theme) => theme.slug),
      ...this.subthemes.map((theme) => theme.slug),
    ];
  }
}

export class ThemeTreeItem extends Tag {
  children: Tag[] = [];

  index = -1;

  constructor(inData: any = null, index = 0) {
    super(inData);
    if (inData instanceof Object) {
      const { children } = inData;
      if (children instanceof Array) {
        this.children = children
          .filter((row) => {
            const rowKeys = Object.keys(row);
            return rowKeys.includes("text") && rowKeys.includes("slug");
          })
          .map((row) => new Tag(row));
      }
    }
    this.index = index;
  }

  get key() {
    return ["theme-item", this.slug, this.index].join("-");
  }
}
